import { useEffect, useRef, useState, type ReactNode } from 'react';
import { fullDate } from '../lib/format';
import { C, EASE_SPRING, glass, HEADER_HEIGHT, ink } from '../lib/theme';
import type { Reason } from '../lib/types';
import type { FilterKey } from '../lib/useFilters';
import { useFilters } from '../lib/useFilters';
import { PeriodSelector } from './PeriodSelector';
import { IconCheck, IconChevronDown } from './icons';

interface Option { value: string; label: string }

/** Exportado pra reuso fora da FilterBar — ex: filtro de período do módulo Clientes. */
export function Pill({ label, value, active, children }: {
  label: string; value: string; active: boolean; children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button" onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', font: 'inherit',
          fontSize: 12.5, fontWeight: 600, padding: '7px 14px', borderRadius: 99,
          border: `1px solid ${active ? 'rgba(255,144,112,0.45)' : ink(0.13)}`,
          background: active ? 'rgba(255,107,71,0.12)' : ink(0.05),
          color: active ? C.fg : ink(0.7),
        }}
      >
        {label}
        <span style={{ fontWeight: 750, color: active ? C.accent : ink(0.45) }}>{value}</span>
        <span style={{ opacity: 0.55, display: 'flex' }}><IconChevronDown size={12} /></span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 9px)', left: 0, zIndex: 80, width: 266,
          ...glass, borderRadius: 16, padding: 12, animation: `koSlideIn 0.22s ${EASE_SPRING}`,
        }}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

function OptionList({ options, current, onPick }: {
  options: Option[]; current?: string; onPick: (v: string | undefined) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 280, overflow: 'auto' }}>
      <button type="button" className="ko-opt" onClick={() => onPick(undefined)} style={rowStyle(!current)}>
        {checkbox(!current)}Todos
      </button>
      {options.map((o) => (
        <button key={o.value} type="button" className="ko-opt" onClick={() => onPick(o.value)} style={rowStyle(current === o.value)}>
          {checkbox(current === o.value)}{o.label}
        </button>
      ))}
    </div>
  );
}

const rowStyle = (active: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', border: 'none', cursor: 'pointer',
  font: 'inherit', textAlign: 'left', fontSize: 13, fontWeight: active ? 750 : 600,
  padding: '8px 10px', borderRadius: 9,
  background: active ? ink(0.09) : 'transparent', color: active ? C.fg : ink(0.75),
});

const checkbox = (on: boolean) => (
  <span style={{
    width: 14, height: 14, borderRadius: 4, flex: 'none',
    border: `1.5px solid ${on ? C.accent : ink(0.25)}`, background: on ? C.accent : 'transparent',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
  }}>{on ? <IconCheck size={10} /> : null}</span>
);

const ZONES: Option[] = [
  { value: 'PowerZap', label: 'PowerZap' },
  { value: 'PowerBot', label: 'PowerBot' },
  { value: 'Kory', label: 'Kory' },
];
const TYPES: Option[] = [
  { value: 'churn', label: 'Churn' },
  { value: 'contraction', label: 'Contraction' },
];
const PLANS: Option[] = [
  { value: 'MENSAL', label: 'Mensal' },
  { value: 'ANUAL', label: 'Anual' },
];

/**
 * Filter Bar (PRD §4 e §6) — pills no padrão do Lovable, com contador de
 * filtros ativos e "Limpar tudo" sempre visíveis. Fica sticky logo abaixo do
 * header e continua interativa mesmo com a lista em skeleton (§9-B).
 */
export function FilterBar({ tab, reasons, categories, sources, partners }: {
  tab: 'fila' | 'confirmados' | 'visao-geral';
  reasons: Reason[];
  categories: string[];
  sources: string[];
  partners: string[];
}) {
  const { filters, setFilter, setRange, clearAll, activeCount } = useFilters();

  const periodLabel = filters.from || filters.to
    ? `${filters.from ? fullDate(filters.from) : '…'} → ${filters.to ? fullDate(filters.to) : '…'}`
    : 'Tudo';

  const pick = (key: FilterKey) => (v: string | undefined) => setFilter(key, v);

  return (
    <div style={{
      position: 'sticky', top: HEADER_HEIGHT, zIndex: 55, display: 'flex', alignItems: 'center', gap: 9,
      flexWrap: 'wrap', padding: '13px 30px', background: 'var(--filterbar-bg)',
      backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)',
      borderBottom: `1px solid ${ink(0.07)}`,
    }}>
      <Pill label="Período" value={periodLabel} active={!!(filters.from || filters.to)}>
        {() => (
          <PeriodSelector
            from={filters.from} to={filters.to}
            onApply={(f, t) => setRange(f, t)}
            onClear={() => setRange(undefined, undefined)}
          />
        )}
      </Pill>

      {/* Segundo eixo de data, exclusivo da aba Confirmados (PRD §8): a data
          filtra por quando a perda ACONTECEU ou por quando foi REVISADA. */}
      {tab === 'confirmados' && (
        <Pill
          label="Data por"
          value={filters.date_field === 'audited_at' ? 'Auditoria' : 'Evento'}
          active={filters.date_field === 'audited_at'}
        >
          {(close) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button type="button" className="ko-opt" style={rowStyle(filters.date_field !== 'audited_at')}
                onClick={() => { setFilter('date_field', undefined); close(); }}>
                {checkbox(filters.date_field !== 'audited_at')}Quando a perda aconteceu
              </button>
              <button type="button" className="ko-opt" style={rowStyle(filters.date_field === 'audited_at')}
                onClick={() => { setFilter('date_field', 'audited_at'); close(); }}>
                {checkbox(filters.date_field === 'audited_at')}Quando foi revisada
              </button>
            </div>
          )}
        </Pill>
      )}

      {filters.channel === 'parceiro' && (
        <Pill label="Parceiro" value={filters.partner_name ?? 'Todos'} active={!!filters.partner_name}>
          {(close) => (
            <OptionList
              options={partners.map((p) => ({ value: p, label: p }))}
              current={filters.partner_name}
              onPick={(v) => { pick('partner_name')(v); close(); }}
            />
          )}
        </Pill>
      )}

      <Pill label="Zona" value={filters.zone ?? 'Todas'} active={!!filters.zone}>
        {(close) => <OptionList options={ZONES} current={filters.zone} onPick={(v) => { pick('zone')(v); close(); }} />}
      </Pill>

      <Pill label="Categoria" value={filters.product_category ?? 'Todas'} active={!!filters.product_category}>
        {(close) => (
          <OptionList
            options={categories.map((c) => ({ value: c, label: c }))}
            current={filters.product_category}
            onPick={(v) => { pick('product_category')(v); close(); }}
          />
        )}
      </Pill>

      <Pill label="Tipo" value={filters.event_type ?? 'Todos'} active={!!filters.event_type}>
        {(close) => <OptionList options={TYPES} current={filters.event_type} onPick={(v) => { pick('event_type')(v); close(); }} />}
      </Pill>

      <Pill label="Plano" value={filters.plan_frequency ?? 'Todos'} active={!!filters.plan_frequency}>
        {(close) => <OptionList options={PLANS} current={filters.plan_frequency} onPick={(v) => { pick('plan_frequency')(v); close(); }} />}
      </Pill>

      <Pill label="Fonte" value={filters.source_system ?? 'Todas'} active={!!filters.source_system}>
        {(close) => (
          <OptionList
            options={sources.map((s) => ({ value: s, label: s }))}
            current={filters.source_system}
            onPick={(v) => { pick('source_system')(v); close(); }}
          />
        )}
      </Pill>

      <Pill
        label="Motivo"
        value={reasons.find((r) => r.id === filters.reason_id)?.label ?? (filters.is_exception ? 'Só exceções' : 'Todos')}
        active={filters.reason_id !== undefined || filters.is_exception !== undefined}
      >
        {(close) => (
          <div>
            <button type="button" className="ko-opt" style={rowStyle(filters.is_exception === true)}
              onClick={() => { setFilter('is_exception', filters.is_exception === true ? undefined : true); close(); }}>
              {checkbox(filters.is_exception === true)}⚠ Só exceções
            </button>
            <div style={{ height: 1, background: ink(0.1), margin: '7px 0' }} />
            <OptionList
              options={reasons.map((r) => ({ value: String(r.id), label: `${r.is_exception ? '⚠ ' : ''}${r.label}` }))}
              current={filters.reason_id !== undefined ? String(filters.reason_id) : undefined}
              onPick={(v) => { setFilter('reason_id', v ? Number(v) : undefined); close(); }}
            />
          </div>
        )}
      </Pill>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginLeft: 'auto' }}>
        <span style={{
          fontSize: 11.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase',
          color: activeCount > 0 ? C.accent : ink(0.3),
        }}>
          {activeCount === 0 ? 'sem filtro' : `${activeCount} filtro${activeCount === 1 ? '' : 's'} ativo${activeCount === 1 ? '' : 's'}`}
        </span>
        <button type="button" onClick={clearAll} className="ko-clear" style={{
          border: 'none', background: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12,
          fontWeight: 600, color: ink(0.45), textDecoration: 'underline', textUnderlineOffset: 3,
        }}>Limpar tudo</button>
      </div>
    </div>
  );
}
